import React, { useMemo, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight, Plus, X } from "react-feather"

import MobileHeader from "../components/MobileHeader"
import useMobileSections, { ResolvedSection } from "../useMobileSections"
import { GET_MOBILE_LIST } from "../queries"
import { cellText, num } from "../format"
import { readPath } from "../config"
import QuickDateFilter, { QuickRange, rangeToParams } from "../components/QuickDateFilter"

/**
 * Ro'yxat sahifasi — barcha bo'limlar uchun bitta komponent.
 *
 * Ustunlar va sahifadagi yozuvlar soni bo'lim ta'rifidan va admin
 * sozlamasidan keladi. Shu sababli yangi bo'lim qo'shish uchun bu
 * faylga tegish shart emas.
 *
 * Tezlik uchun: bir vaqtda faqat bitta sahifa yuklanadi, oldingi
 * natija saqlanib turadi (keepPreviousData), ma'lumot yarim daqiqa
 * yangi hisoblanadi.
 */
export default function MobileSection() {
  const { code } = useParams<{ code: string }>()
  const { t } = useTranslation()
  const history = useHistory()
  const { sections } = useMobileSections()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [searchOpen, setSearchOpen] = useState(false)
  const [range, setRange] = useState<QuickRange>("all")

  const section: ResolvedSection | undefined = useMemo(
    () => sections.find((s) => s.code === code),
    [sections, code]
  )

  const { data, isLoading, isFetching } = useQuery(
    ["MOBILE_LIST", code, page, search, range, section?.pageSize],
    () =>
      GET_MOBILE_LIST({
        url: (section as ResolvedSection).url,
        pageNumber: page,
        pageSize: (section as ResolvedSection).pageSize,
        searchText: search || undefined,
        extra: rangeToParams(range),
      }),
    {
      enabled: !!section,
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  )

  if (!section) {
    return (
      <div className="mb-page">
        <MobileHeader title={t("mobSection")} showBack />
        <div className="mb-empty">{t("mobNoAccess")}</div>
      </div>
    )
  }

  // Bo'limda sana ustuni bormi — davr filtrini ko'rsatish shunga bog'liq
  const hasDateField = section.fields.some(
    (f) => f.type === "date" || f.type === "datetime"
  )

  const rows: any[] = data?.data || []
  const totalPages: number = data?.totalPages || 1
  const totalRecords: number = data?.totalRecords || 0

  const totalValue = section.totalField
    ? readPath(data, section.totalField.path)
    : undefined

  return (
    <div className="mb-page">
      <MobileHeader
        title={t(section.labelKey)}
        onSearch={() => setSearchOpen((v) => !v)}
        onMenu={() => history.push("/m/more")}
      />

      {searchOpen && (
        <div className="mb-search">
          <input
            autoFocus
            value={search}
            placeholder={String(t("search"))}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
          {search && (
            <button type="button" onClick={() => setSearch("")}>
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/*
        Davr filtri faqat sanali bo'limlarda ko'rinadi. Omborlar yoki
        tovarlar ro'yxatida "shu oy" degan filtr ma'nosiz bo'lardi.
      */}
      {hasDateField && (
        <QuickDateFilter
          value={range}
          onChange={(v) => {
            setRange(v)
            setPage(1)
          }}
        />
      )}

      <div className="mb-body">
        {/* Jadval: birinchi ustun tartib raqami */}
        <div className="mb-table">
          <div
            className="mb-tr mb-tr--head"
            style={{ gridTemplateColumns: gridTemplate(section), minWidth: gridMinWidth(section) }}
          >
            <span>№</span>
            {section.visibleFields.map((f) => (
              <span key={f.key} className={f.align === "right" ? "mb-right" : ""}>
                {t(f.labelKey)}
              </span>
            ))}
          </div>

          {isLoading ? (
            <div className="mb-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="mb-skeleton-row" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="mb-empty">{t("noData")}</div>
          ) : (
            rows.map((row, i) => (
              <div
                key={row.id ?? i}
                className="mb-tr"
                style={{ gridTemplateColumns: gridTemplate(section), minWidth: gridMinWidth(section) }}
              >
                <span className="mb-idx">
                  {(page - 1) * section.pageSize + i + 1}
                </span>
                {section.visibleFields.map((f) => {
                  const text = cellText(row, f)
                  /*
                    Sana va vaqt telefonda bitta qatorga sig'maydi va
                    "13.08.2026…" bo'lib kesilib qolardi. Namunadagi kabi
                    sana tepada, vaqt ostida ko'rsatiladi.
                  */
                  if (f.type === "datetime" && text.includes(" ")) {
                    const [d, tm] = text.split(" ")
                    return (
                      <span key={f.key} className="mb-cell mb-cell--stack" title={text}>
                        <b>{d}</b>
                        <i>{tm}</i>
                      </span>
                    )
                  }
                  return (
                    <span
                      key={f.key}
                      className={`mb-cell${f.align === "right" ? " mb-right" : ""}${
                        f.type === "money" ? " mb-money" : ""
                      }`}
                      title={text}
                    >
                      {text}
                    </span>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Jami */}
        {section.totalField && rows.length > 0 && (
          <div className="mb-total">
            <div className="mb-total-label">
              {t(section.totalField.labelKey)}
              <span className="mb-total-count">
                {totalRecords} {t("mobRecords")}
              </span>
            </div>
            <div className="mb-total-value">
              {section.totalField.type === "money"
                ? `$${num(totalValue)}`
                : num(totalValue)}
            </div>
          </div>
        )}

        {/* Sahifalash */}
        {totalPages > 1 && (
          <div className="mb-pager">
            <button
              type="button"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={17} />
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={17} />
            </button>
          </div>
        )}
      </div>

      {/* Yangi yozuv — web sahifasidagi shakl ochiladi */}
      {section.createPath && (
        <button
          type="button"
          className="mb-fab"
          aria-label="add"
          onClick={() => history.push(section.createPath as string)}
        >
          <Plus size={22} />
        </button>
      )}
    </div>
  )
}

/** Ustunlar kengligi: raqam ustuni tor, pul ustuni o'ngga tekislangan */
/**
 * Ustun turiga qarab eng kichik kenglik. Jadval tuzilishi ham, uning
 * eng kichik umumiy kengligi ham shu jadvaldan hisoblanadi — ikkitasi
 * bir-biridan ayrilib qolmasin.
 */
const COLUMN_MIN: Record<string, number> = {
  money: 78,
  number: 46,
  status: 44,
  datetime: 74,
  list: 120,
  date: 74,
  text: 70,
}

const INDEX_COL = 26
const CELL_GAP = 7

function colMin(type: string): number {
  return COLUMN_MIN[type] ?? COLUMN_MIN.text
}

function gridTemplate(section: ResolvedSection): string {
  const cols = section.visibleFields.map((f) => {
    const min = colMin(f.type)
    const grow =
      f.type === "money" ? 0.9 : f.type === "number" ? 0.5 : f.type === "status" ? 0.4
      : f.type === "list" ? 1.4
      : f.type === "datetime" || f.type === "date" ? 0.8 : 1
    return `minmax(${min}px, ${grow}fr)`
  })
  return `${INDEX_COL}px ${cols.join(" ")}`
}

/**
 * Jadvalning eng kichik kengligi.
 *
 * Busiz ustunlar ko'paygan bo'limlarda (masalan qoldiqlar yoki
 * logistika) jadval telefon ekraniga sig'may, chetlari KESILIB
 * qolardi — grid o'z eng kichik o'lchamidan tor konteynerdan chiqib
 * ketadi, konteyner esa uni yashiradi. Endi aniq kenglik beriladi va
 * jadval o'ngga suriladi: hech bir ustun yo'qolmaydi.
 */
function gridMinWidth(section: ResolvedSection): number {
  const cells = section.visibleFields.reduce((sum, f) => sum + colMin(f.type), 0)
  const gaps = CELL_GAP * section.visibleFields.length
  // 22px — qatorning chap va o'ng ichki bo'shlig'i
  return INDEX_COL + cells + gaps + 22
}
